import { z } from 'zod';
import { baseModelSchema } from '../../model/baseModel';
import { ITemperatureUnitFormValues } from './temperatureUnitFormValues';

/**
 * Zod schema for validating temperature unit data.
 * Extends the base model schema with temperature unit-specific fields.
 *
 * @property {string} deviceNumber - Identifier of the temperature monitoring device
 * @property {number} stockId - ID of the stock being monitored
 * @property {number} tripId - ID of the trip this temperature unit is assigned to
 */
export const temperatureUnitSchema = baseModelSchema.extend({
    deviceNumber: z.string().nonempty('Device number is required'),
    stockId: z.number().min(0, 'Stock ID is required'),
    tripId: z.number().min(0, 'Trip ID is required'),
});

/**
 * TypeScript interface for temperature unit data.
 * Generated from the Zod schema for type safety.
 */
export type ITemperatureUnit = z.infer<typeof temperatureUnitSchema>;

/**
 * Creates a validated Temperature Unit object from form values.
 *
 * @param {ITemperatureUnitFormValues} formValues - The form data to convert
 * @returns {ITemperatureUnit} A validated temperature unit object
 * @throws {ZodError} If validation fails
 */
export const TemperatureUnit = (formValues : ITemperatureUnitFormValues) : ITemperatureUnit => {
    return temperatureUnitSchema.parse({
        id: formValues.id,
        guid: formValues.guid,
        isActive: formValues.isActive,
        deviceNumber: formValues.deviceNumber,
        tripId: Number(formValues.trip?.value) ?? 0,
        stockId: Number(formValues.stock?.value) ?? 0,
    });
};
